import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";
import { Tooltip } from "@mui/material";
import { getConsultantOnlineTimeApi } from "../../services/consultantManagement";
import { toastMessage } from "../../utils/toastMessage";

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
];

const typeLabel = (type) => {
  switch (type) {
    case "CHAT":
      return "Chat online";
    case "CALL":
      return "Call online";
    case "VIDEO":
      return "Video online";
    case "BOTH":
      return "Chat + Call both ON";
    case "ANY":
      return "Available";
    case "APP":
      return "App open";
    case "BUSY_CHAT":
      return "Busy in chat";
    case "BUSY_CALL":
      return "Busy in call";
    case "BUSY_VIDEO":
      return "Busy in video";
    default:
      return type;
  }
};

const formatClock = (value) => {
  if (!value) return "open";
  const date = moment(value).tz("Asia/Kolkata");
  if (!date.isValid()) return "—";
  return date.format("DD MMM, h:mm A");
};

const Pill = ({ on, label }) => (
  <span className={`cons-ot-pill ${on ? "on" : "off"}`}>
    <span className="cons-ot-pill-dot" />
    {label}
  </span>
);

const Row = ({ label, value, tooltip }) => (
  <div className="cons-ot-row">
    {tooltip ? (
      <Tooltip title={tooltip} arrow placement="top">
        <div className="cons-ot-row-label cons-ot-row-label-help">{label}</div>
      </Tooltip>
    ) : (
      <div className="cons-ot-row-label">{label}</div>
    )}
    <div className="cons-ot-row-value">{value || "0m"}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="cons-ot-card">
    {title ? <div className="cons-ot-card-title">{title}</div> : null}
    {children}
  </div>
);

const ConsultantOnlineTime = ({ consultantId: consultantIdProp }) => {
  const { id: consultantIdFromRoute } = useParams();
  const consultantId = consultantIdProp || consultantIdFromRoute;
  const [range, setRange] = useState("today");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showAllTimeline, setShowAllTimeline] = useState(false);

  const fetchData = useCallback(async (nextRange, signal) => {
    if (!consultantId) return;
    setLoading(true);
    try {
      const { data: res } = await getConsultantOnlineTimeApi(
        consultantId,
        { range: nextRange },
        { signal }
      );
      if (signal?.aborted) return;
      if (res?.success || res?.code === 200) {
        setData(res.data || res.result || {});
      } else {
        setData(null);
        toastMessage(res?.message || "Failed to fetch online time", "error", "OnlineTime");
      }
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      toastMessage("Failed to fetch online time", "error", "OnlineTime");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [consultantId]);

  useEffect(() => {
    const controller = new AbortController();
    setShowAllTimeline(false);
    fetchData(range, controller.signal);
    return () => controller.abort();
  }, [range, fetchData]);

  const formatted = data?.formatted || {};
  const totals = data?.totals || {};
  const counts = data?.counts || {};
  const completedUsers = data?.completedUsers || {};
  const percentages = data?.percentages || {};
  const live = data?.live || {};
  const usual = data?.usualOnlineHours || {};
  const timeline = data?.timeline || [];
  const visibleTimeline = showAllTimeline ? timeline : timeline.slice(0, 5);
  const maxHourCount = Math.max(1, ...(usual.any?.buckets || []).map((b) => Number(b.count) || 0));

  const liveBusyParts = [];
  if (live.chatBusyNow) liveBusyParts.push(`Chat ${Math.round(live.chatBusyElapsedMinutes || 0)}m`);
  if (live.callBusyNow) liveBusyParts.push(`Call ${Math.round(live.callBusyElapsedMinutes || 0)}m`);
  if (live.videoBusyNow) liveBusyParts.push(`Video ${Math.round(live.videoBusyElapsedMinutes || 0)}m`);
  const liveBusyLabel = liveBusyParts.length ? liveBusyParts.join(" · ") : "Not in a consultation";

  return (
    <div className="cons-ot-panel">
      <div className="cons-ot-header">
        <div>
          <h5>Online Time</h5>
          <div className="cons-ot-sub">{data?.rangeLabel || "IST"} · same clock as consultant app</div>
        </div>
        <div className="cons-ot-ranges">
          {RANGES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`cons-ot-range ${range === item.id ? "active" : ""}`}
              onClick={() => setRange(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="cons-ot-loading">Loading online time…</div>
      ) : (
        <>
          <Card title="Right now">
            <div className="cons-ot-pills">
              <Pill on={!!live.chat} label={`Chat ${live.chat ? "ON" : "OFF"}`} />
              <Pill on={!!live.call} label={`Call ${live.call ? "ON" : "OFF"}`} />
              <Pill on={!!live.both} label={`Both ${live.both ? "ON" : "OFF"}`} />
              <Pill on={!!live.app} label={`App ${live.app ? "open" : "closed"}`} />
              {live.video ? <Pill on label="Video ON" /> : null}
            </div>
            <Row
              label="Current available session"
              value={live.any ? `${Math.round(live.anyElapsedMinutes || 0)}m` : "Offline"}
            />
            <Row label="Currently busy" value={liveBusyLabel} />
          </Card>

          <div className="cons-ot-grid cons-ot-grid-3">
            <Card title={`Available · ${data?.rangeLabel || ""}`}>
              <Row label="Total available" value={formatted.anyOnline} />
              <Row label="Chat online" value={formatted.chatOnline} />
              <Row label="Call online" value={formatted.callOnline} />
              <Row label="Chat + Call both ON" value={formatted.bothOnline} />
              <Row label="Chat only" value={formatted.chatOnly} />
              <Row label="Call only" value={formatted.callOnly} />
              {(totals.videoOnlineMinutes || 0) > 0 ? (
                <Row label="Video online" value={formatted.videoOnline} />
              ) : null}
            </Card>

            <Card title="Busy in consultations">
              <Row label="Total busy" value={formatted.totalBusy} />
              <Row label="Busy in chat" value={formatted.chatBusy} />
              <Row label="Busy in call" value={formatted.callBusy} />
              {(totals.videoBusyMinutes || 0) > 0 ? (
                <Row label="Busy in video" value={formatted.videoBusy} />
              ) : null}
            </Card>

            <Card title="App usage">
              <Row label="App open / connected" value={formatted.appUsage} />
              <Row label="Avg app-open session" value={formatted.avgAppSession} />
            </Card>
          </div>

          <div className="cons-ot-grid">
            <Card title="Averages">
              <Row
                label="Avg available / day"
                value={formatted.avgDailyAny}
                tooltip={`Total available (${formatted.anyOnline || "0m"}) ÷ ${data?.days || 1} selected day(s).`}
              />
              <Row
                label="Avg chat online / day"
                value={formatted.avgDailyChat}
                tooltip={`Chat online (${formatted.chatOnline || "0m"}) ÷ ${data?.days || 1} selected day(s). Chat + Call overlap remains part of Chat online.`}
              />
              <Row
                label="Avg call online / day"
                value={formatted.avgDailyCall}
                tooltip={`Call online (${formatted.callOnline || "0m"}) ÷ ${data?.days || 1} selected day(s). Chat + Call overlap remains part of Call online.`}
              />
              <Row
                label="Avg available session"
                value={formatted.avgAnySession}
                tooltip={`Total available (${formatted.anyOnline || "0m"}) ÷ ${counts.any || 0} available session(s).`}
              />
              <Row
                label="Avg chat session"
                value={formatted.avgChatSession}
                tooltip={`Chat online (${formatted.chatOnline || "0m"}) ÷ ${counts.chat || 0} chat session(s).`}
              />
              <Row
                label="Avg call session"
                value={formatted.avgCallSession}
                tooltip={`Call online (${formatted.callOnline || "0m"}) ÷ ${counts.call || 0} call session(s).`}
              />
              <Row
                label="Avg both-ON session"
                value={formatted.avgBothSession}
                tooltip={`Both-ON time (${formatted.bothOnline || "0m"}) ÷ ${counts.both || 0} both-ON session(s).`}
              />
              <Row
                label="Avg talk time / completed user"
                value={formatted.avgTalk}
                tooltip={`Total busy (${formatted.totalBusy || "0m"}) ÷ ${completedUsers.total || 0} unique user(s) who completed a consultation in this period.`}
              />
              <Row
                label="Avg chat talk / completed user"
                value={formatted.avgChatTalk}
                tooltip={`Busy in chat (${formatted.chatBusy || "0m"}) ÷ ${completedUsers.chat || 0} unique user(s) who completed chat in this period.`}
              />
              <Row
                label="Avg call talk / completed user"
                value={formatted.avgCallTalk}
                tooltip={`Busy in call (${formatted.callBusy || "0m"}) ÷ ${completedUsers.call || 0} unique user(s) who completed call in this period.`}
              />
              <Row
                label="Overall busy / available"
                value={`${Number(percentages.busyOfAvailable || 0).toFixed(2)}%`}
                tooltip={`Total busy (${formatted.totalBusy || "0m"}) ÷ total available (${formatted.anyOnline || "0m"}) × 100.`}
              />
              <Row
                label="Chat busy / online"
                value={`${Number(percentages.chatBusyOfOnline || 0).toFixed(2)}%`}
                tooltip={`Busy in chat (${formatted.chatBusy || "0m"}) ÷ chat online (${formatted.chatOnline || "0m"}) × 100.`}
              />
              <Row
                label="Call busy / online"
                value={`${Number(percentages.callBusyOfOnline || 0).toFixed(2)}%`}
                tooltip={`Busy in call (${formatted.callBusy || "0m"}) ÷ call online (${formatted.callOnline || "0m"}) × 100.`}
              />
              <Row
                label="Chat + Call busy / online"
                value={`${Number(percentages.combinedBusyOfOnline || 0).toFixed(2)}%`}
                tooltip="(Chat busy + Call busy) ÷ unique Chat/Call online time × 100. Chat + Call overlap is counted once in the denominator."
              />
            </Card>

            <Card title="When they usually come online">
              <Row
                label="Most common hour"
                value={usual.any?.peakHourLabel || "—"}
                tooltip={`Groups ${usual.any?.sampleCount || 0} availability-session start(s) by IST hour in the selected period. The hour with the highest number of starts is shown.`}
              />
              <Row
                label="Chat usually from"
                value={usual.chat?.peakHourLabel || "—"}
                tooltip={`Groups ${usual.chat?.sampleCount || 0} Chat-toggle session start(s) by IST hour. The most frequently occurring start hour is shown.`}
              />
              <Row
                label="Call usually from"
                value={usual.call?.peakHourLabel || "—"}
                tooltip={`Groups ${usual.call?.sampleCount || 0} Call-toggle session start(s) by IST hour. The most frequently occurring start hour is shown.`}
              />
              <Row
                label="App usually opened at"
                value={usual.app?.peakHourLabel || "—"}
                tooltip={`Groups ${usual.app?.sampleCount || 0} app-connected session start(s) by IST hour. The most frequently occurring app-open hour is shown.`}
              />
              {(usual.any?.buckets || []).length ? (
                <div className="cons-ot-hours">
                  {usual.any.buckets.map((bucket) => (
                    <Tooltip
                      key={bucket.hour}
                      arrow
                      placement="top"
                      title={`${bucket.count} availability session(s) started between ${bucket.label} and the next hour. Bar = ${bucket.count} ÷ ${maxHourCount} peak starts × 100.`}
                    >
                      <div className="cons-ot-hour">
                        <span className="cons-ot-hour-label">{bucket.label}</span>
                        <span className="cons-ot-hour-bar-wrap">
                          <span
                            className="cons-ot-hour-bar"
                            style={{ width: `${Math.max(8, (Number(bucket.count) / maxHourCount) * 100)}%` }}
                          />
                        </span>
                        <span className="cons-ot-hour-count">{bucket.count}x</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <div className="cons-ot-empty">No session starts in this period</div>
              )}
            </Card>
          </div>

          <Card title="Recent sessions">
            {!visibleTimeline.length ? (
              <div className="cons-ot-empty">No sessions in this period</div>
            ) : (
              visibleTimeline.map((item, index) => (
                <div key={`${item.type}-${item.startTime}-${index}`} className="cons-ot-session">
                  <div>
                    <div className="cons-ot-session-type">{typeLabel(item.type)}</div>
                    <div className="cons-ot-session-time">
                      {formatClock(item.startTime)} → {item.isOpen ? "now" : formatClock(item.endTime)}
                    </div>
                  </div>
                  <div className="cons-ot-session-dur">
                    {item.durationLabel || "0m"}
                    {item.isOpen ? " · live" : ""}
                  </div>
                </div>
              ))
            )}
            {timeline.length > 5 ? (
              <button
                type="button"
                className="cons-ot-more"
                onClick={() => setShowAllTimeline((v) => !v)}
              >
                {showAllTimeline ? "Show less" : `Show all ${timeline.length} sessions`}
              </button>
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
};

export default ConsultantOnlineTime;
